import {Component, OnInit, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {environment} from '@environments/environment';
import {ListView, TypeComparaison, TypeFilter} from '@domain/common/list-view';
import {Sorting} from '@domain/common/list-view/sorting';
import {TypeAction} from '@share/component/floating-button/floating-button';
import {ContratComptesItemComponent} from './contrat-comptes-item.component';
import {Compte} from '@domain/contrat/compte';
import {ContratButtonService} from '@services/admin/contrat/contrat.button.service';

/**
 * Composant de gestion des comptes d'un contrat
 *
 * @author Yannick Gauthier
 * @date 18/08/2025
 */
@Component({
    host: {'data-test-id': 'contrat-comptes'},
    selector: 'contrat-comptes',
    templateUrl: './contrat-comptes.component.html'
})
export class ContratComptesComponent implements OnInit, OnDestroy {
    /** Liste view des comptes */
    comptesListView: ListView<Compte, ContratComptesItemComponent>;

    /** ID du contrat */
    idContrat: number;
    
    /** ID du fournisseur */
    idFournisseur: number;
    
    /** Indique si une sauvegarde est en cours */
    isSaving: boolean = false;

    /**
     * Constructeur
     * @param translateService Service de traduction
     * @param router Service de routage
     * @param activatedRoute Route activée
     * @param contratButtonService Service de gestion des boutons
     */
    constructor(
        public translateService: TranslateService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private contratButtonService: ContratButtonService
    ) {}

    /**
     * Initialisation
     */
    ngOnInit(): void {
        //Récupération des paramètres de la route
        this.idContrat = +this.activatedRoute.parent.snapshot.params.idContrat;
        this.idFournisseur = +this.activatedRoute.parent.snapshot.queryParams.idFournisseur;

        //Initialisation de la liste view
        this.comptesListView = new ListView<Compte, ContratComptesItemComponent>({
            component: ContratComptesItemComponent,
            uri: `${environment.baseUrl}/controller/Contrat/listeCarteByContrat/${this.idContrat}`,
            title: this.translateService.instant('admin.frais.fournisseurs.compte.title'),
            defaultOrder: 'numCarteClair',
            listeFilters: [{
                clef: 'dateFin',
                title: this.translateService.instant('admin.frais.fournisseurs.compte.validite'),
                type: TypeFilter[TypeFilter.DATE],
                typeComparaison: TypeComparaison[TypeComparaison.BETWEEN],
                isDefault: true
            }, {
                clef: 'user.matricule',
                title: this.translateService.instant('admin.frais.fournisseurs.compte.matricule'),
                isDefault: true,
                typeComparaison: TypeComparaison[TypeComparaison.LIKE]
            }, {
                clef: 'isActif',
                title: this.translateService.instant('admin.frais.fournisseurs.compte.actif'),
                type: TypeFilter[TypeFilter.BOOLEAN],
                typeComparaison: TypeComparaison[TypeComparaison.EQUAL]
            }],
            isFilter: true
        });

        //Définition des colonnes de tri
        this.comptesListView.columns = [
            {key: 'dateFin', title: this.translateService.instant('admin.frais.fournisseurs.compte.validite')},
            {key: 'user.matricule', title: this.translateService.instant('admin.frais.fournisseurs.compte.matricule')},
            {key: 'isActif', title: this.translateService.instant('admin.frais.fournisseurs.compte.actif')}
        ];

        //Ajout du tri de la liste selon l'ordre voulu
        this.comptesListView.sorting = new Sorting(
            this.comptesListView.columns,
            'numCarteClair'
        );

        //Nom de la classe
        this.comptesListView.className = "contrat-comptes";

        //Persistence des filtres
        this.comptesListView.isPersistFilters = true;

        //Définition de la liste des actions possibles et mise à jour via le service
        const actions = [
            {
                type: TypeAction.PRIMARY,
                icone: 'nio icon-ajouter',
                libelle: 'global.actions.ajouter',
                doAction: () => this.creerCompte()
            }
        ];
        
        //Mise à jour des actions via le service pour que le composant parent les récupère
        this.contratButtonService.setActions(actions);
    }

    /**
     * Destruction
     */
    ngOnDestroy(): void {
        //Réinitialisation des actions lors de la destruction du composant
        this.contratButtonService.setActions([]);
    }

    /**
     * Méthode appelée lors de la création d'un nouveau compte
     */
    creerCompte(): void {
        this.router.navigate(['Admin/Frais/Factures/Contrat', this.idContrat, 'Compte', 0], {
            queryParams: { idFournisseur: this.idFournisseur }
        });
    }
}
